import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, OutlinedInput, Paper, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography} from "@material-ui/core";
import {useAdvancedExportStyles} from "../../../export/advanced_export";
import EnhancedSelect from "../../../../components/EnhancedSelect";
import {MappingArrow, SearcherIcon} from "../../../../utils/icons";
import {useGlobalStyles} from "../../../../utils/styles";
import {useAdvancedSupplierImportStyles} from "../advanced_supplier_import";
import {Ballot, Clear} from "@material-ui/icons";
import CatalogCheckbox from "../../../../components/EnhancedCheckbox";
import NavigationButtons from "./NavigationButtons";
import ErrorIcon from "@material-ui/icons/Error";
import cloneDeep from 'lodash/cloneDeep';
import {Notifier} from "../../../../../documents/document_components";
import {SUPPLIER_SYNDICATION_ENABLED, useDebouncedEffect} from "../../../../utils/utils";
import AttributeMapper from "./components/AttributeMapper";
import Fuse from "fuse.js";
import ColumnRuleEditor from "./components/ColumnRuleEditor";

const ConditionalCheckbox = (props) => {
    const {validationRules} = props;

    return (
        <CatalogCheckbox
            checked={isStrictlyRequired(validationRules)}
        />
    )
}

export const isConditionallyRequired = (validationRules) => {
    if (validationRules) {
        if(validationRules.find((rule) => {
            return rule.Type === "required";
        })) return true;
    }

    return false;
}

export const isStrictlyRequired = (validationRules) => {
    if (validationRules) {
        if(validationRules.find((rule) => {
            return rule.Type === "required" && rule.Conditions === undefined;
        })) return true;
    }

    return false;
}

export default function AttributeMapping(props) {
    const {allowBack, allowNext, backStep, nextStep, activeStep, steps, setSteps, setError} = props;
    const {profile, setProfile, formattedProfile, profileType} = props;
    const {fileParsedFields, sampleData} = props;
    const {retailerAttributes, isMarketplace} = props;
    const {refreshView} = props;

    const [parsedFields, setParsedFields] = useState(fileParsedFields);

    const globalClasses = useGlobalStyles();
    const advancedExportClasses = useAdvancedExportStyles();
    const advancedImportStyles = useAdvancedSupplierImportStyles();

    const [unmappedSupplierAttributes, setUnmappedSupplierAttributes] = useState(parsedFields);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [previousSupplierFields, setPreviousSupplierFields] = useState(parsedFields);
    const [previousRetailerAttributes, setPreviousRetailerAttributes] = useState(retailerAttributes);

    const [currentView, setCurrentView] = useState("all"); // all || retailer || supplier

    const [advancedAttributeMapperOpen, setAdvancedAttributeMapperOpen] = useState(false);
    const [columnRuleEditorOpen, setColumnRuleEditorOpen] = useState(false);
    const [selectedColumnRule, setSelectedColumnRule] = useState(null);
    const [selectedColumnCalculated, setSelectedColumnCalculated] = useState(false);
    const [groupedRules, setGroupedRules] = useState({});

    const [addCalculatedColumnOpen, setAddCalculatedColumnOpen] = useState(false);
    const [newCalculatedColumnName, setNewCalculatedColumnName] = useState("");

    //region Pagination Logic
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(20);
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    //endregion

    //region Helper Functions
    const getAllUnmappedRetailerAttributes = useCallback((retailerAttributes, profile) => {
        const calculatedColumns = getAllCalculatedColumns();
        const unmappedAttributes = retailerAttributes.filter((attribute) => !profile.AttributeMappings[attribute.Name]);
        return [...calculatedColumns, ...unmappedAttributes];

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile.AttributeMappingRules]);

    const getAllUnmappedSupplierAttributes = useCallback((parsedFields, profile) => {
        return parsedFields
            .filter((field) => !Object.values(profile.AttributeMappings).includes(field.field))
            .filter(field => profile.AttributeMappingRules.find(rule => rule.TargetField === field.field) === undefined);
    }, []);

    const getAllCalculatedColumns = () => {
        return profile.AttributeMappingRules
            .filter((rule) => {
                return (!retailerAttributes.find((attribute) => attribute.Name === rule.TargetField))
            })
            .map((rule) => (
                {
                    IsCalculatedColumn: true,
                    Name: rule.TargetField,
                    FriendlyName: rule.TargetField,
                    Type: "text",
                    Description: "Calculated column.",
                    Options: ["string"],
                    Group: "Base",
                    System: false,
                    Tags: "",
                    Hidden: false,
                    HiddenFromPartner: false,
                    DestinationId: "string",
                    CustomAttributes: "string",
                    SyncStatus: "Not Synced",
                    Editor: "",
                    ValidationRules: [],
                    LastModified: "2024-09-20T14:56:45.9101699+00:00"
                }
            ));
    }

    const search = useCallback((term) => {
        let operatingAttributes = currentView === "supplier" ? parsedFields : retailerAttributes;

        if (!term) {
            if (currentView === "supplier") {
                setPreviousSupplierFields(getAllUnmappedSupplierAttributes(parsedFields, profile));
            } else if (currentView === "retailer") {
                setPreviousRetailerAttributes(getAllUnmappedRetailerAttributes(retailerAttributes, profile));
            } else {
                setPreviousRetailerAttributes([...getAllCalculatedColumns(), ...retailerAttributes]);
            }
            return;
        }

        const termLower = term.toLowerCase();
        const matches = operatingAttributes.filter((attribute) => {
            if (currentView === "supplier") {
                return attribute.field.toLowerCase().includes(termLower);
            }
            return (
                attribute.Name.toLowerCase().includes(termLower) ||
                attribute.FriendlyName.toLowerCase().includes(termLower) ||
                (attribute.Description?.toLowerCase().includes(termLower) ?? false)
            );
        });

        if (currentView === "supplier") {
            setPreviousSupplierFields(matches);
        } else {
            setPreviousRetailerAttributes(matches);
        }

        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentView, parsedFields, retailerAttributes, profile]);

    const fuse = useMemo(() => {
        const fuseOpts = {
            includeScore: true,
            findAllMatches: false,
            ignoreLocation: true,
            threshold: 0.1
        };

        return new Fuse(retailerAttributes.map(attribute => attribute.Name), fuseOpts);
    }, [retailerAttributes]);

    const autoMatch = useCallback(() => {
        const matchErrors = [];
        const mappings = {};

        parsedFields.forEach((field) => {
            if (!Object.values(profile.AttributeMappings).includes(field.field)) {
                const result = fuse.search(field.field);
                if (result.length > 0) {
                    mappings[result[0].item] = field.field;
                } else {
                    matchErrors.push(field.field);
                }
            }
        });

        if (matchErrors.length > 0) {
            setError({
                open: true,
                errorHeader: "Auto Match Error",
                errorMessage: `The following attributes could not be auto matched to retailer attributes: ${matchErrors.join(", ")}`
            });
        }

        setProfile(prev => ({
            ...prev,
            AttributeMappings: {
                ...prev.AttributeMappings,
                ...mappings
            }
        }));
    }, [parsedFields, profile.AttributeMappings, fuse, setError, setProfile]);

    const onNext = () => {
        let unmappedSupplierAttributes = [];
        let unmappedRetailerAttributes = [];

        // Check if any supplier attributes are unmapped
        // parsedFields.forEach((field) => {
        //     if (Object.values(profile.AttributeMappings).indexOf(field.field) === -1)
        //         unmappedSupplierAttributes.push(field.field);
        // });

        // Check if any required retailer attributes are unmapped
        retailerAttributes.forEach((attribute) => {
            if (attribute.ValidationRules) {
                if (attribute.ValidationRules.find((rule) => rule.Type === "required")) {
                    if (!profile.AttributeMappings[attribute.Name]) {
                        unmappedRetailerAttributes.push(attribute.Name);
                    }
                }
            }
        });

        if (unmappedSupplierAttributes.length > 0 || unmappedRetailerAttributes.length > 0) {
            let errors = [];

            if (unmappedSupplierAttributes.length > 0) {
                let msg1 = `Please map the following supplier attributes: ${unmappedSupplierAttributes.join(", ")}.`;
                errors.push(msg1);
            }

            if (unmappedRetailerAttributes.length > 0) {
                let msg2 = `The following retailer attributes are required: ${unmappedRetailerAttributes.join(", ")}.`;
                errors.push(msg2);
            }

            setSteps((prevState) => {
                let newState = cloneDeep(prevState);
                newState[activeStep].status = "warning";
                newState[activeStep].errors = errors;
                return newState;
            });
        } else {
            setSteps((prevState) => {
                let newState = cloneDeep(prevState);
                newState[activeStep].status = "success";
                newState[activeStep].errors = undefined;
                return newState;
            });
        }
    }
    //endregion

    //region Row Rendering
    const displayRetailerAttributes = React.useMemo(() => {
        return previousRetailerAttributes
            .sort((a, b) => {
                // Calculated columns should be shown first
                if(a.IsCalculatedColumn && !b.IsCalculatedColumn) return -1;
                if(!a.IsCalculatedColumn && b.IsCalculatedColumn) return 1;

                if (isConditionallyRequired(a.ValidationRules) && !isConditionallyRequired(b.ValidationRules)) return -1;
                if (!isConditionallyRequired(a.ValidationRules) && isConditionallyRequired(b.ValidationRules)) return 1;

                if (profile.AttributeMappings[a.Name] && !profile.AttributeMappings[b.Name]) return -1;
                if (!profile.AttributeMappings[a.Name] && profile.AttributeMappings[b.Name]) return 1;

                return 0;
            })
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((attribute, index) => {
                let mappedSupplierAttribute = profile.AttributeMappings[attribute.Name];
                let mappedRule = profile.AttributeMappingRules.find((rule) => rule.TargetField === attribute.Name);
                let mappingBtnDisabled = ((mappedSupplierAttribute && !(mappedSupplierAttribute && mappedRule)) && !attribute.IsCalculatedColumn);

                return (
                    <TableRow key={index} className={globalClasses.tableRow}>
                        {(isMarketplace && SUPPLIER_SYNDICATION_ENABLED) && (
                            <TableCell style={{padding: "5px", border: "none"}}>
                                <Button
                                    disabled={mappingBtnDisabled}
                                    variant={groupedRules[attribute.Name] ? "contained" : "outlined"}
                                    style={{
                                        width: "40px",
                                        minWidth: "40px",
                                        height: "40px",
                                        backgroundColor: mappingBtnDisabled
                                            ? "white"
                                            : groupedRules[attribute.Name]
                                                ? "#006BFF"
                                                : "white",
                                        color: mappingBtnDisabled
                                            ? "#CECECEFF"
                                            : groupedRules[attribute.Name]
                                                ? "white"
                                                : "black",
                                        borderColor: mappingBtnDisabled
                                            ? "#CECECEFF"
                                            : groupedRules[attribute.Name]
                                                ? "#006BFF"
                                                : "black"
                                    }}
                                    onClick={() => {
                                        setColumnRuleEditorOpen(true);
                                        setSelectedColumnRule(attribute.Name);
                                        setSelectedColumnCalculated(attribute.IsCalculatedColumn);
                                    }}
                                >
                                    <Ballot />
                                </Button>
                            </TableCell>
                        )}

                        <TableCell className={advancedImportStyles.mappingCell}
                                   style={{padding: "12px 0px 12px 24px"}}>
                            <Grid container justify={"space-between"} alignItems={"center"}
                                  style={{display: "flex", gap: "12px"}}>
                                {groupedRules[attribute.Name] ? (
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        backgroundColor: "lightblue",
                                        opacity: 0.8,
                                        border: "#006BFF solid 1px",
                                        borderRadius: "4px",
                                        flex: 1,
                                        height: "40px"
                                    }}>
                                        <Typography variant={"subtitle2"} style={{color: "#006BFF", fontWeight: "bold"}}>
                                            {groupedRules[attribute.Name].length} Rule(s)
                                        </Typography>
                                    </div>
                                ) : (
                                    <EnhancedSelect
                                        placeholder={"Supplier Attribute"}
                                        isClearable
                                        options={parsedFields.map((field, index) => {
                                            return {
                                                label: field.field,
                                                value: field.field
                                            }
                                        })}
                                        value={{
                                            label: mappedSupplierAttribute,
                                            value: mappedSupplierAttribute
                                        }}
                                        onChange={(e) => {
                                            if (e == null) {
                                                const copiedProfile = cloneDeep(profile);
                                                delete copiedProfile.AttributeMappings[attribute.Name];
                                                setProfile(copiedProfile);
                                                return;
                                            }

                                            setProfile({
                                                ...profile,
                                                AttributeMappings: {
                                                    ...profile.AttributeMappings,
                                                    [attribute.Name]: e.value
                                                }
                                            });
                                        }}
                                    />
                                )}

                                <MappingArrow/>
                            </Grid>
                        </TableCell>

                        <TableCell className={advancedImportStyles.mappingCell}
                                   style={{padding: "12px 12px 12px 12px"}}>
                            {attribute.IsCalculatedColumn ? (
                                <div style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "lightblue",
                                    opacity: 0.8,
                                    border: "#006BFF solid 1px",
                                    borderRadius: "4px",
                                    flex: 1,
                                    height: "40px"
                                }}>
                                    <Typography variant={"subtitle2"} style={{color: "#006BFF", fontWeight: "bold"}}>
                                        calc: {attribute.FriendlyName}
                                    </Typography>
                                </div>
                            ) : (
                                <Typography>
                                    {attribute.FriendlyName}
                                </Typography>
                            )}
                        </TableCell>

                        <TableCell className={advancedImportStyles.mappingCell}
                                   style={{padding: "12px 12px 12px 12px"}}>
                            <ConditionalCheckbox
                                validationRules={attribute.ValidationRules}/>
                        </TableCell>

                        <TableCell className={advancedImportStyles.mappingCell}
                                   style={{padding: "12px 12px 12px 12px"}}>
                            <Typography>
                                {attribute.Type ?? ""}
                            </Typography>
                        </TableCell>

                        <TableCell className={advancedImportStyles.mappingCell}
                                   style={{padding: "12px 12px 12px 12px"}}>
                            <Typography>
                                {attribute.Description ?? ""}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )
            })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshView, previousRetailerAttributes, retailerAttributes, profile.AttributeMappings, currentView, page, rowsPerPage, parsedFields]);

    const displaySupplierAttributes = React.useMemo(() => {
        return previousSupplierFields
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((field, index) => {
                let foundMappedAttribute = retailerAttributes.find((attribute) => {
                    return profile.AttributeMappings[attribute.Name] === field.field;
                });
                if (!foundMappedAttribute) foundMappedAttribute = {
                    Name: "",
                    FriendlyName: "",
                    ValidationRules: undefined,
                    Type: "",
                    Description: ""
                }

                return (
                    <TableRow key={index} className={globalClasses.tableRow}>
                        <TableCell className={advancedImportStyles.mappingCell}
                                   style={{padding: "12px 0px 12px 24px"}}>
                            <Grid container justify={"space-between"} alignItems={"center"}
                                  style={{display: "flex", gap: "12px"}}>
                                <Typography>
                                    {field.field}
                                </Typography>

                                <MappingArrow/>
                            </Grid>
                        </TableCell>

                        <TableCell className={advancedImportStyles.mappingCell}
                                   style={{padding: "12px 12px 12px 12px"}}>
                            {groupedRules[field.Name] ? (
                                <div style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "lightblue",
                                    opacity: 0.8,
                                    border: "#006BFF solid 1px",
                                    borderRadius: "4px",
                                    flex: 1,
                                    height: "40px"
                                }}>
                                    <Typography variant={"subtitle2"} style={{color: "#006BFF", fontWeight: "bold"}}>
                                        {groupedRules[field.Name].length} Rule(s)
                                    </Typography>
                                </div>
                            ) : (
                                <EnhancedSelect
                                    placeholder={"Retailer Attribute"}
                                    isClearable
                                    options={retailerAttributes.map((attribute, index) => {
                                        return {
                                            label: attribute.FriendlyName,
                                            value: attribute.Name
                                        }
                                    })}
                                    value={{
                                        label: foundMappedAttribute?.FriendlyName ?? "",
                                        value: foundMappedAttribute?.Name ?? ""
                                    }}
                                    onChange={(e) => {
                                        let key = "";
                                        let value = "";

                                        if (e == null) {
                                            const copiedProfile = cloneDeep(profile);
                                            delete copiedProfile.AttributeMappings[foundMappedAttribute.Name];
                                            setProfile(copiedProfile);
                                            return;
                                        } else if (profile.AttributeMappings[e.value] != null) {
                                            key = foundMappedAttribute.Name ?? "";
                                            value = field.field;
                                        } else {
                                            key = e.value ?? "";
                                            value = field.field;
                                        }

                                        // If retailer attribute is already mapped to another supplier attribute, overwrite the previous mapping
                                        if(key === "") key = e.value;

                                        let prevValue = profile.AttributeMappings[foundMappedAttribute.Name];
                                        if (prevValue != null) {
                                            let prevKey = Object.keys(profile.AttributeMappings).find(key => profile.AttributeMappings[key] === field.field);
                                            if (prevKey != null) {
                                                setProfile({
                                                    ...profile,
                                                    AttributeMappings: {
                                                        ...profile.AttributeMappings,
                                                        [prevKey]: null
                                                    }
                                                });
                                            }
                                        } else {
                                            setProfile({
                                                ...profile,
                                                AttributeMappings: {
                                                    ...profile.AttributeMappings,
                                                    [key]: value
                                                }
                                            });
                                        }
                                    }}
                                />
                            )}
                        </TableCell>

                        <TableCell className={advancedImportStyles.mappingCell}
                                   style={{padding: "12px 12px 12px 12px"}}>
                            <ConditionalCheckbox
                                validationRules={foundMappedAttribute.ValidationRules}/>
                        </TableCell>

                        <TableCell className={advancedImportStyles.mappingCell}
                                   style={{padding: "12px 12px 12px 12px"}}>
                            <Typography>
                                {foundMappedAttribute.Type ?? ""}
                            </Typography>
                        </TableCell>

                        <TableCell className={advancedImportStyles.mappingCell}
                                   style={{padding: "12px 12px 12px 12px"}}>
                            <Typography>
                                {foundMappedAttribute.Description ?? ""}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )
            })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshView, previousSupplierFields, retailerAttributes, profile.AttributeMappings, currentView, page, rowsPerPage, parsedFields]);
    //endregion

    //region Lifecycle
    useDebouncedEffect(() => {
        search(searchTerm);
    }, [searchTerm], 500);

    useEffect(() => {
        setUnmappedSupplierAttributes(parsedFields.filter((field) => {
            const mapping = !Object.values(profile.AttributeMappings).includes(field.field);
            const mappingRule = profile.AttributeMappingRules.find((rule) => rule.TargetField === field.field) === undefined;
            return mapping && mappingRule;
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile.AttributeMappings, refreshView]);

    useEffect(() => {
        // Update parsed fields to select from
        const newParsedFields = [
            ...fileParsedFields,
            ...profile.AttributeMappingRules.map((rule) => ({
                field: rule.TargetField,
                content: ""
            }))
        ];
        setParsedFields(newParsedFields)

        // Update grouped rules
        const newGroupedRules = {};
        profile.AttributeMappingRules.forEach((rule) => {
            if(!newGroupedRules[rule.TargetField]) newGroupedRules[rule.TargetField] = [];
            newGroupedRules[rule.TargetField].push(rule);
        });
        setGroupedRules(newGroupedRules);

        const renderedCalculatedColumns = getAllCalculatedColumns();

        // Update displayed attributes to include calculated columns and ensure they are shown first
        if(currentView === "retailer") {
            setPreviousRetailerAttributes((prev) => [
                ...renderedCalculatedColumns.filter((column) => {
                    return !prev.find((attribute) => attribute.Name === column.Name)
                }),
                ...prev
            ]);
        }
        else {
            setPreviousRetailerAttributes([
                ...renderedCalculatedColumns,
                ...retailerAttributes
            ]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile.AttributeMappingRules, refreshView]);

    useEffect(() => {
        onNext();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    //endregion

    return (
        <Grid container style={{gap: "24px"}}>
            {(steps[activeStep].status === "error" || steps[activeStep].errors) && (
                <>
                    {steps[activeStep].errors.map((error, index) => {
                        return (
                            <Card key={index} id="cueMessage"
                                  style={{backgroundColor: '#ee3224', color: 'white', width: "100%"}}>
                                <CardContent style={{display: 'flex', alignItems: 'center'}}>
                                    <ErrorIcon style={{marginRight: '1rem'}}/>
                                    <Typography style={{
                                        textAlign: "left",
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                        maxHeight: "100px"
                                    }}>
                                        {error}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )
                    })}
                </>
            )}

            <Paper style={{width: "100%", padding: "24px", borderRadius: "5px"}}>
                <Grid container style={{gap: "12px"}}>
                    <Grid container justify={"space-between"}>
                        <Typography className={advancedExportClasses.sectionTitle}
                                    style={{marginBottom: "12px"}}>
                            Attribute Mapping
                        </Typography>

                        <Grid item style={{gap: "6px", display: "flex"}}>
                                <Button
                                    variant={"outlined"}
                                    onClick={() => setAdvancedAttributeMapperOpen(true)}
                                >
                                    Advanced Mapping Tools
                                </Button>
                            {(isMarketplace && SUPPLIER_SYNDICATION_ENABLED) && (
                                <Button
                                    variant={"outlined"}
                                    onClick={() => {
                                        setAddCalculatedColumnOpen(true)
                                    }}
                                >
                                    Add Calculated Column
                                </Button>
                            )}

                            <Button
                                className={currentView === "retailer" ? globalClasses.actionButton : globalClasses.outlinedButton}
                                onClick={() => {
                                    setSearchTerm("");
                                    setPreviousRetailerAttributes(getAllUnmappedRetailerAttributes(retailerAttributes, profile));
                                    setCurrentView("retailer");
                                    setPage(0);
                                }}
                            >
                                Unmapped Retailer Attributes
                            </Button>

                            <Button
                                className={currentView === "supplier" ? globalClasses.actionButton : globalClasses.outlinedButton}
                                onClick={() => {
                                    setSearchTerm("");
                                    setPreviousSupplierFields(getAllUnmappedSupplierAttributes(parsedFields, profile));
                                    setCurrentView("supplier");
                                    setPage(0)
                                }}
                            >
                                Your Unmapped Attributes
                            </Button>

                            <Button
                                className={currentView === "all" ? globalClasses.actionButton : globalClasses.outlinedButton}
                                onClick={() => {
                                    setSearchTerm("");
                                    setPreviousRetailerAttributes([
                                        ...getAllCalculatedColumns(),
                                        ...retailerAttributes
                                    ]);
                                    setCurrentView("all");
                                }}
                            >
                                All Attributes
                            </Button>
                        </Grid>
                    </Grid>

                    <Grid container style={{flexDirection: "column"}}>
                        {/*Check if there are any unmapped supplier attributes*/}
                        {unmappedSupplierAttributes.length > 0 && (
                            <Notifier color="#FFB600" key={0}
                                      style={{marginBottom: '5px', flex: 1, position: "relative"}}
                                      barstyles={{
                                          height: "100%", position: "absolute"
                                      }}
                                      messagestyles={{
                                          marginLeft: "5px"
                                      }}
                            >
                                {props.isRetailer ? "Your unmapped attributes" : "Unmapped supplier attributes:"} <span> </span>
                                {unmappedSupplierAttributes
                                    .map((field, index) => {
                                        return (
                                            <span
                                                key={index}>{field.field}{index === unmappedSupplierAttributes.length - 1 ? "" : ", "}</span>
                                        )
                                    })}
                            </Notifier>
                        )}
                    </Grid>

                    <Grid container>
                        <OutlinedInput
                            placeholder={"Search"}
                            value={searchTerm}
                            style={{width: "100%"}}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                // search(e.target.value);
                            }}
                            className={globalClasses.search}
                            startAdornment={
                                <InputAdornment style={{cursor: "pointer"}} position="start"
                                                onClick={() => search()}>
                                    <SearcherIcon/>
                                </InputAdornment>
                            }
                            endAdornment={
                                <>
                                    {searchTerm && (
                                        <InputAdornment style={{cursor: "pointer"}} position="end"
                                                        onClick={() => {
                                                            setSearchTerm("");
                                                            search();
                                                        }}>
                                            <Clear style={{width: "20px", height: "20px", opacity: "0.8"}}/>
                                        </InputAdornment>
                                    )}
                                </>
                            }
                        />
                    </Grid>

                    <Grid container>
                        <Table style={{tableLayout: "fixed"}}>
                            <TableHead>
                                <TableRow>
                                    {(isMarketplace && SUPPLIER_SYNDICATION_ENABLED && currentView !== "supplier") && (
                                        <TableCell style={{border: "none", width: "40px", padding: 0}}>
                                            {/*PLACEHOLDER FOR RULES BUTTON*/}
                                        </TableCell>
                                    )}

                                    <TableCell style={{border: "none"}}>
                                        <Grid container alignItems={"center"} style={{gap: "12px"}}>
                                            <Typography className={advancedImportStyles.mappingHeader}>
                                                Your Attribute
                                            </Typography>

                                            <Button className={globalClasses.outlinedActionButton}
                                                    style={{
                                                        height: "32px",
                                                        lineHeight: 1
                                                    }}
                                                    onClick={() => autoMatch()}
                                            >
                                                Auto Match
                                            </Button>
                                        </Grid>
                                    </TableCell>

                                    <TableCell style={{border: "none", padding: "12px 12px 12px 12px"}}>
                                        <Grid container alignItems={"center"} style={{gap: "12px"}}>
                                            <Typography className={advancedImportStyles.mappingHeader}>
                                                Retailer Attribute
                                            </Typography>
                                        </Grid>
                                    </TableCell>

                                    <TableCell style={{border: "none", padding: "12px 12px 12px 12px"}}>
                                        <Grid container alignItems={"center"} style={{gap: "12px"}}>
                                            <Typography className={advancedImportStyles.mappingHeader}>
                                                Required
                                            </Typography>
                                        </Grid>
                                    </TableCell>

                                    <TableCell style={{border: "none", padding: "12px 12px 12px 12px"}}>
                                        <Grid container alignItems={"center"} style={{gap: "12px"}}>
                                            <Typography className={advancedImportStyles.mappingHeader}>
                                                Type
                                            </Typography>
                                        </Grid>
                                    </TableCell>

                                    <TableCell style={{border: "none", padding: "12px 12px 12px 12px"}}>
                                        <Grid container alignItems={"center"} style={{gap: "12px"}}>
                                            <Typography className={advancedImportStyles.mappingHeader}>
                                                Description
                                            </Typography>
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {currentView === "supplier" ? displaySupplierAttributes : displayRetailerAttributes}
                            </TableBody>
                        </Table>
                        <TablePagination
                            style={{float: "right", width: "100%"}}
                            rowsPerPageOptions={[20, 50, 100]}
                            component="div"
                            count={currentView === "supplier" ? previousSupplierFields.length : previousRetailerAttributes.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <NavigationButtons
                allowBack={allowBack}
                allowNext={allowNext}
                backStep={backStep}
                nextStep={nextStep}
                onNext={onNext}
                activeStep={activeStep}
                steps={steps}

                formattedProfile={formattedProfile}
                profileType={profileType}
            />

            {advancedAttributeMapperOpen && (
                <AttributeMapper
                    attributes={retailerAttributes}
                    parsedFields={parsedFields}
                    open={advancedAttributeMapperOpen}
                    setOpen={setAdvancedAttributeMapperOpen}
                    isRetailer={props.isRetailer}
                    profile={profile}
                    setProfile={setProfile}
                />
            )}

            {columnRuleEditorOpen && (
                <ColumnRuleEditor
                    open={columnRuleEditorOpen}
                    setOpen={setColumnRuleEditorOpen}
                    attributes={retailerAttributes}
                    sampleData={sampleData}
                    parsedFields={parsedFields}
                    profile={profile}
                    setProfile={setProfile}
                    formattedProfile={formattedProfile}
                    column={selectedColumnRule}
                    isCalculatedColumn={selectedColumnCalculated}
                    groupedRule={groupedRules[selectedColumnRule]}
                />
            )}

            {addCalculatedColumnOpen && (
                <Dialog
                    open={addCalculatedColumnOpen}
                    onClose={() => {
                        setAddCalculatedColumnOpen(false)
                        setNewCalculatedColumnName("")
                    }}
                    maxWidth={"sm"}
                >
                    <DialogTitle style={{padding: "8px"}}>
                        Add Calculated Column
                    </DialogTitle>

                    <DialogContent style={{paddingTop: 8, paddingBottom: 8, overflow: "hidden"}}>
                        <OutlinedInput
                            style={{height: "35px"}}
                            placeholder={"New Column Name"}
                            value={newCalculatedColumnName}
                            onChange={(e) => setNewCalculatedColumnName(e.target.value)}
                        />
                    </DialogContent>

                    <DialogActions>
                        <Button
                            variant={"outlined"}
                            onClick={() => {
                                setAddCalculatedColumnOpen(false)
                                setNewCalculatedColumnName("")
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant={"outlined"}
                            onClick={() => {
                                setProfile((prevProfile) => {
                                    return {
                                        ...prevProfile,
                                        AttributeMappingRules: [
                                            ...prevProfile.AttributeMappingRules,
                                            {
                                                TargetField: newCalculatedColumnName,
                                                SourceField: "",
                                                Condition: ""
                                            }
                                        ]
                                    }
                                })
                                setAddCalculatedColumnOpen(false)
                            }}
                        >
                            Add
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Grid>
    )
}
